import { useEffect, useRef } from 'react';

// Must match ADMIN_SENTINEL in lambda/layer/nodejs/wsChannels.js. The two
// runtimes cannot share a module, so this pair is by convention: change one,
// change the other, or the admin Dashboard silently stops receiving anything.
export const ADMIN_SENTINEL = '__admin__';

/**
 * The one WebSocket connection implementation.
 *
 * Owns plumbing only - connect, ping, reconnect, cleanup. Message handling
 * belongs to the caller, which keeps each page's bespoke logic (debouncing,
 * batch suppression, pending-operation guards) where it already lives.
 *
 * $connect is authorised, so a token is required. It travels in the query
 * string because browsers cannot set headers on a WebSocket handshake - never
 * log the assembled URL.
 *
 * @param {string}   leadId           channel to open; ADMIN_SENTINEL for cross-project
 * @param {string}   timestamp        session identifier, stored on the connection row
 * @param {string}   token            Cognito ID token - admin or client (captured once)
 * @param {function} getToken         read the CURRENT token; called on every
 *                                    connect INCLUDING reconnects. Prefer this
 *                                    over `token`: a captured token expires
 *                                    after an hour and every reconnect then
 *                                    bounces off the authorizer forever while
 *                                    the HTTP side keeps refreshing (BUG-030,
 *                                    2026-08-21 - admin risk/issue live
 *                                    updates died in any session over an hour)
 * @param {function} onMessage        called with each parsed message
 * @param {boolean}  enabled          skip connecting when false
 * @param {number}   pingIntervalMs   keepalive interval
 * @param {number}   reconnectDelayMs delay before reconnecting after a close
 * @param {number}   connectDelayMs   delay before the first connect
 */
export function useProjectWebSocket({
    leadId,
    timestamp,
    token,
    getToken,
    onMessage,
    enabled = true,
    pingIntervalMs = 30000,
    reconnectDelayMs = 5000,
    connectDelayMs = 0
}) {
    const wsRef = useRef(null);
    // Held in a ref so a caller passing an inline function does not re-run the
    // effect on every render, which would tear the connection down in a loop
    const onMessageRef = useRef(onMessage);
    // Same ref treatment: getToken is called inside connect(), never in deps
    const getTokenRef = useRef(getToken);

    useEffect(() => {
        onMessageRef.current = onMessage;
    }, [onMessage]);

    useEffect(() => {
        getTokenRef.current = getToken;
    }, [getToken]);

    useEffect(() => {
        if (!enabled || !leadId || !timestamp || (!token && !getTokenRef.current)) return;

        let mounted = true;
        let pingTimer = null;
        let reconnectTimer = null;

        const connect = () => {
            if (!mounted) return;

            // Read the token NOW, not at effect time: reconnects can be hours
            // after mount, and the captured one is long expired by then
            const liveToken = getTokenRef.current ? getTokenRef.current() : token;
            if (!liveToken) return;

            const url = `${process.env.REACT_APP_WS_URL}`
                + `?leadId=${encodeURIComponent(leadId)}`
                + `&timestamp=${encodeURIComponent(String(timestamp))}`
                + `&token=${encodeURIComponent(liveToken)}`;
            console.log('WebSocket connecting for:', leadId);

            const ws = new WebSocket(url);
            wsRef.current = ws;

            ws.onopen = () => {
                console.log('WebSocket connected:', leadId);
                pingTimer = setInterval(() => {
                    if (ws.readyState === WebSocket.OPEN) {
                        ws.send(JSON.stringify({ action: 'ping' }));
                    }
                }, pingIntervalMs);
            };

            ws.onmessage = (event) => {
                try {
                    const message = JSON.parse(event.data);
                    if (onMessageRef.current) {
                        onMessageRef.current(message);
                    }
                } catch (err) {
                    console.error('WebSocket message parse error:', err);
                }
            };

            ws.onclose = (event) => {
                console.log('WebSocket closed:', event.code);
                if (pingTimer) {
                    clearInterval(pingTimer);
                    pingTimer = null;
                }
                if (mounted) {
                    reconnectTimer = setTimeout(connect, reconnectDelayMs);
                }
            };

            ws.onerror = (error) => {
                console.error('WebSocket error:', error);
            };
        };

        const startTimer = setTimeout(connect, connectDelayMs);

        return () => {
            mounted = false;
            clearTimeout(startTimer);
            if (pingTimer) clearInterval(pingTimer);
            if (reconnectTimer) clearTimeout(reconnectTimer);
            if (wsRef.current) wsRef.current.close();
        };
    }, [leadId, timestamp, token, enabled, pingIntervalMs, reconnectDelayMs, connectDelayMs]);

    return wsRef;
}

/**
 * The admin Dashboard's cross-project channel.
 *
 * Every other consumer rides a single project's leadId. The Dashboard spans
 * all clients, so it subscribes to the sentinel instead; notifyProjectUpdate
 * always includes it, and the authorizer allows it only for admin tokens.
 */
export function useAdminWebSocket(onMessage, enabled = true) {
    // Stable for the component's lifetime - a fresh value each render would
    // change the effect's deps and reconnect in a loop
    const timestampRef = useRef(String(Date.now()));

    return useProjectWebSocket({
        leadId: ADMIN_SENTINEL,
        timestamp: timestampRef.current,
        getToken: () => localStorage.getItem('adminToken'),
        onMessage,
        enabled
    });
}

export default useProjectWebSocket;
